/**
 * MIRRORS `freeze-v2/data/dwsCopy.js` (slots, MIME maps, caps, mb/ext),
 * `freeze-v2/initiate/IniRaiseDrawer.js` (formatWords and the two FORMATS
 * words it derives) and `freeze-v2/data/sopCopy.js` (POLICY.preliminary →
 * CLOSING_NOTE).
 *
 * COPIED, NOT IMPORTED. disputes-v2 and freeze-v2 are deliberately independent
 * modules: neither may import from the other, so shared copy travels as a copy.
 *
 * Divergence from the originals: the DWS_ prefix is dropped (no DWS exists in
 * this module), DECLARATION and GOVERNS_SENTENCE move here from
 * SkuIntelligence.js where they were module-private, and only the constants the
 * freeze drawer actually needs are carried over — DWS_STEPS, DWS_RETAIN,
 * DWS_API_TIP, DWS_TWO_SOURCES and MISSING_LABEL describe screens this module
 * does not have.
 */
import {fmtKg} from '../format'

/**
 * Order is load-bearing: buildEvidenceFiles emits images in this order, not in
 * fill order, so two identical sets recorded in a different sequence read the
 * same afterwards. It is also the order `data/scanImage.js`'s
 * merchantProofImage(row, idx) uses for `idx % 5`.
 *
 * The keys are freeze's (len/wid/hgt/scl/lbl) rather than EvidenceIntake.js's
 * (length/width/height/scale/label). EvidenceIntake's SHOTS is module-private
 * and unexported, so the two cannot be shared anyway; keeping freeze's keys
 * keeps this file diffable against the original it was copied from.
 */
export const SHOT_SLOTS = [
  {k: 'len', label: 'Package length', hint: 'Tape along the long side'},
  {k: 'wid', label: 'Package width', hint: 'Tape across the width'},
  {k: 'hgt', label: 'Package height', hint: 'Tape up the height'},
  {k: 'scl', label: 'Shipment on weighing scale', hint: 'Display readable'},
  {k: 'lbl', label: 'Package with label', hint: 'AWB barcode in focus'},
]

export const IMAGE_TYPES = {
  'image/jpeg': 'JPG', 'image/jpg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WebP',
}

export const VIDEO_TYPES = {
  'video/mp4': 'MP4', 'video/quicktime': 'MOV', 'video/webm': 'WebM',
}

/** The extensions, not the MIME types — two entries share 'JPG', so this dedupes. */
function formatWords(types) {
  const words = Array.from(new Set(Object.keys(types).map((t) => types[t])))
  return words.slice(0, -1).join(', ') + ' or ' + words[words.length - 1]
}

export const IMAGE_FORMATS = formatWords(IMAGE_TYPES)
export const VIDEO_FORMATS = formatWords(VIDEO_TYPES)

/** Video gets a far larger budget because one continuous clip is the whole point of it. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024
export const MAX_VIDEO_BYTES = 80 * 1024 * 1024

export function mb(bytes) {
  return (bytes / 1048576).toFixed(1) + ' MB'
}

export function ext(name) {
  const i = String(name || '').lastIndexOf('.')
  return i > 0 ? name.slice(i + 1).toUpperCase() : 'unknown'
}

/** Verbatim from the freeze module's own request drawer — the same declaration
 * a merchant signs there, because it is the same commitment. */
export const DECLARATION = 'I confirm the declared packing material will be used for these shipments.'

/**
 * VERBATIM `POLICY.preliminary` from `freeze-v2/data/sopCopy.js`, which is
 * itself verbatim from the source SoP document (`SopPRELIM`). Do not
 * paraphrase, tighten, modernise or "fix" it. If a sentence reads awkwardly,
 * that is the source document, not a typo.
 */
export const CLOSING_NOTE = 'This approval is preliminary for the first 2 billing cycles post weight-freeze. '
  + 'Any deviations over and above will be handled as part of monthly billing.'

/**
 * Which of the two weights decides the bill, AND the figure it decides it at —
 * "governs" says nothing useful without the number the chargeable field is
 * expected to hold. Volumetric governing is the case worth colouring: the bill
 * is then being driven by the packing material rather than by the product, and
 * that is the one a merchant can act on by repacking.
 *
 * Keys match weightFacts().governs. Rule 13: the chip carrying one of these
 * sentences uses sentenceChipSx, never chipSx, or 'kg' renders as 'Kg'.
 */
export const GOVERNS_SENTENCE = {
  dead: (v) => `Dead weight governs at ${fmtKg(v)}`,
  vol: (v) => `Volumetric weight governs at ${fmtKg(v)}`,
  both: (v) => `Dead and volumetric are equal at ${fmtKg(v)}`,
}
